'use client';

import { c as _c } from "react/compiler-runtime";
import { BaseButton } from '@7f-ui/components/button/button';
import { Modal } from '@7f-ui/components/modal/modal';
import { CalendarBlank, PencilSimple, User } from '@phosphor-icons/react';
import { formatDateWithWeekday, formatGermanNumber } from '@utils/utils-format';
import type { PurchasableDay } from '../_hooks/use-app-experience-purchasable-days';
import { useAppTicketSelection, useOpeningHoursLabel } from '../_hooks/use-app-ticket-selection';
import { useExperienceFilterContext } from './app-experience-filter-provider';
import { AppTicketSheetFooter } from './app-ticket-purchase-footer';
type AppTicketSelectionContentProps = {
  day: PurchasableDay | null;
  submitLabel: string;
  isSubmitting?: boolean;
  disabled?: boolean;
  showDateRow?: boolean;
  isDateLoading?: boolean;
  onEditTickets: () => void;
  onEditDate: () => void;
  onSubmit: () => void;
};
export function AppTicketSelectionContent(t0) {
  const $ = _c(22);
  const {
    day,
    submitLabel,
    isSubmitting,
    disabled,
    showDateRow: t1,
    isDateLoading,
    onEditTickets,
    onEditDate,
    onSubmit
  } = t0;
  const showDateRow = t1 === undefined ? true : t1;
  const {
    totalCount,
    ticketsBreakdown
  } = useAppTicketSelection();
  const getOpeningHoursLabel = useOpeningHoursLabel();
  let dateDetail;
  if (showDateRow && day) {
    let t2;
    if ($[0] !== day || $[1] !== getOpeningHoursLabel) {
      t2 = getOpeningHoursLabel(day);
      $[0] = day;
      $[1] = getOpeningHoursLabel;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    dateDetail = t2;
  } else {
    if (showDateRow && isDateLoading) {
      dateDetail = "\xD6ffnungstage werden geladen \u2026";
    }
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <User height={24} width={24} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = totalCount === 1 ? `${formatGermanNumber(totalCount)} Eintrittskarte` : `${formatGermanNumber(totalCount)} Eintrittskarten`;
  let t4;
  if ($[4] !== onEditTickets || $[5] !== t3 || $[6] !== ticketsBreakdown) {
    t4 = <SheetRow icon={t2} title={t3} detail={ticketsBreakdown} onEdit={onEditTickets} editLabel={"Eintrittskarten \xE4ndern"} />;
    $[4] = onEditTickets;
    $[5] = t3;
    $[6] = ticketsBreakdown;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== dateDetail || $[9] !== day || $[10] !== onEditDate || $[11] !== showDateRow) {
    t5 = showDateRow && <SheetRow icon={<CalendarBlank height={24} width={24} />} title={day ? formatDateWithWeekday(day.date) : "Datum w\xE4hlen"} detail={dateDetail} onEdit={onEditDate} editLabel={day ? "Datum \xE4ndern" : "Datum w\xE4hlen"} />;
    $[8] = dateDetail;
    $[9] = day;
    $[10] = onEditDate;
    $[11] = showDateRow;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== disabled || $[14] !== isSubmitting || $[15] !== onSubmit || $[16] !== submitLabel) {
    t6 = <AppTicketSheetFooter submitLabel={submitLabel} isSubmitting={isSubmitting} disabled={disabled} onSubmit={onSubmit} />;
    $[13] = disabled;
    $[14] = isSubmitting;
    $[15] = onSubmit;
    $[16] = submitLabel;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <div className="grid gap-6 p-6" data-testid="TicketSelection.sheet">{t4}{t5}{t6}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
type AppTicketSelectionSheetProps = AppTicketSelectionContentProps & {
  isOpen: boolean;
  onClose: () => void;
};
export function AppTicketSelectionSheet(t0) {
  const $ = _c(14);
  let contentProps;
  let isOpen;
  let onClose;
  if ($[0] !== t0) {
    ({
      isOpen,
      onClose,
      ...contentProps
    } = t0);
    $[0] = t0;
    $[1] = contentProps;
    $[2] = isOpen;
    $[3] = onClose;
  } else {
    contentProps = $[1];
    isOpen = $[2];
    onClose = $[3];
  }
  const {
    experience
  } = useExperienceFilterContext();
  let t1;
  if ($[4] !== experience.title || $[5] !== onClose) {
    t1 = <Modal.Header onClose={onClose}>{experience.title}</Modal.Header>;
    $[4] = experience.title;
    $[5] = onClose;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== contentProps) {
    t2 = <AppTicketSelectionContent {...contentProps} />;
    $[7] = contentProps;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== isOpen || $[10] !== onClose || $[11] !== t1 || $[12] !== t2) {
    t3 = <Modal isOpen={isOpen} onClose={onClose}>{t1}{t2}</Modal>;
    $[9] = isOpen;
    $[10] = onClose;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
type SheetRowProps = {
  icon: React.ReactNode;
  title: string;
  detail?: string;
  editLabel: string;
  onEdit: () => void;
};
function SheetRow(t0) {
  const $ = _c(17);
  const {
    icon,
    title,
    detail,
    editLabel,
    onEdit
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="flex size-12 items-center justify-center rounded-xl bg-sand">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <span className="font-body-copy font-semibold uppercase">{title}</span>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== detail) {
    t3 = detail && <span className="font-body-small text-secondary-text">{detail}</span>;
    $[4] = detail;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="grid flex-1">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <PencilSimple height={20} width={20} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== editLabel || $[11] !== onEdit) {
    t6 = <BaseButton onClick={onEdit} aria-label={editLabel} className="p-2">{t5}</BaseButton>;
    $[10] = editLabel;
    $[11] = onEdit;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t1 || $[14] !== t4 || $[15] !== t6) {
    t7 = <div className="flex items-center gap-4">{t1}{t4}{t6}</div>;
    $[13] = t1;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}